import { doc, onSnapshot, serverTimestamp, updateDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import { syncProfileGoalTrackersForChild } from "../features/tracking/profileGoalTrackerService";

const getCurrentUserId = () => auth.currentUser?.uid || "";

const normalizeText = (value = "") => String(value || "").trim();

const getCareGoals = (supportProfile = {}) => (
  Array.isArray(supportProfile?.careGoals) ? supportProfile.careGoals : []
);

const updateProfileGoals = async ({
  childId = "",
  childName = "",
  supportProfile = {},
  nextGoals = [],
  deps = {},
} = {}) => {
  const cleanChildId = normalizeText(childId);
  if (!cleanChildId) {
    throw new Error("Missing child id.");
  }

  const firestoreDb = deps.db || db;
  const docFn = deps.doc || doc;
  const update = deps.updateDoc || updateDoc;
  const timestamp = deps.serverTimestamp || serverTimestamp;

  const nextSupportProfile = {
    ...(supportProfile || {}),
    careGoals: nextGoals,
  };

  await update(docFn(firestoreDb, "children", cleanChildId), {
    supportProfile: nextSupportProfile,
    updatedAt: timestamp(),
    updatedBy: deps.userId || getCurrentUserId(),
  });
  const syncGoalTrackers = deps.syncProfileGoalTrackersForChild || syncProfileGoalTrackersForChild;
  try {
    await syncGoalTrackers({
      childId: cleanChildId,
      childName,
      goals: nextGoals,
      deps,
    });
  } catch (error) {
    // Profile goals are authoritative; tracker synchronization is a derived,
    // recoverable side effect and must not invalidate the completed save.
    deps.onTrackerSyncError?.(error);
  }

  return nextSupportProfile;
};

export const updateProfileGoal = async ({
  childId = "",
  childName = "",
  supportProfile = {},
  goalId = "",
  updates = {},
  deps = {},
} = {}) => {
  const cleanGoalId = normalizeText(goalId);
  if (!cleanGoalId) {
    throw new Error("Missing goal id.");
  }

  const goals = getCareGoals(supportProfile);
  const nextGoals = goals.map((goal) => (
    goal.id === cleanGoalId ? { ...goal, ...updates } : goal
  ));

  return updateProfileGoals({
    childId,
    childName,
    supportProfile,
    nextGoals,
    deps,
  });
};

export const removeProfileGoal = async ({
  childId = "",
  childName = "",
  supportProfile = {},
  goalId = "",
  deps = {},
} = {}) => {
  const cleanGoalId = normalizeText(goalId);
  if (!cleanGoalId) {
    throw new Error("Missing goal id.");
  }

  const nextGoals = getCareGoals(supportProfile).filter((goal) => goal.id !== cleanGoalId);

  return updateProfileGoals({
    childId,
    childName,
    supportProfile,
    nextGoals,
    deps,
  });
};

export const restoreProfileGoal = async ({
  childId = "",
  childName = "",
  supportProfile = {},
  goal = null,
  deps = {},
} = {}) => {
  const cleanGoalId = normalizeText(goal?.id);
  if (!cleanGoalId) {
    throw new Error("Missing goal id.");
  }

  const goals = getCareGoals(supportProfile);
  const nextGoals = goals.some((existingGoal) => existingGoal.id === cleanGoalId)
    ? goals
    : [...goals, goal];

  return updateProfileGoals({
    childId,
    childName,
    supportProfile,
    nextGoals,
    deps,
  });
};

export const subscribeToChildSupportProfile = ({
  childId = "",
  onSupportProfile = () => {},
  onError = () => {},
  deps = {},
} = {}) => {
  const cleanChildId = normalizeText(childId);
  if (!cleanChildId) {
    onSupportProfile({});
    return () => {};
  }

  const firestoreDb = deps.db || db;
  const docFn = deps.doc || doc;
  const snapshotFn = deps.onSnapshot || onSnapshot;
  return snapshotFn(
    docFn(firestoreDb, "children", cleanChildId),
    (snapshot) => onSupportProfile(snapshot.exists()
      ? snapshot.data()?.supportProfile || {}
      : {}),
    onError
  );
};
